import { isNative } from "@/lib/native";
import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";

const ADSENSE_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT || "";

// Shows a Google AdSense unit to free users only. Premium subscribers are ad-free.
export default function AdBanner(props) {
  if (isNative) return null;
  return <AdBannerWeb {...props} />;
}

function AdBannerWeb({ slot, onSubscribe, className = "" }) {
  const { user } = useAuth();
  const insRef = useRef(null);
  const isPremium = user?.is_premium;

  useEffect(() => {
    if (isPremium || !ADSENSE_CLIENT || !slot) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      /* adsbygoogle not ready yet */
    }
  }, [isPremium, slot]);

  // Premium = ad-free: render nothing.
  if (isPremium) return null;

  // Needs BOTH a publisher ID and an ad-unit slot to serve a real ad unit.
  const ready = ADSENSE_CLIENT && slot;

  // Not fully configured yet — show a tasteful placeholder so the ad slot is
  // visible in the design and users can choose to go ad-free.
  if (!ready) {
    return (
      <div
        data-testid="ad-banner-placeholder"
        className={`relative overflow-hidden rounded-2xl border-2 border-dashed border-[#D4AF37]/35 bg-[#0B0F19]/70 ${className}`}
      >
        <div className="bg-[#D4AF37]/10 border-b border-dashed border-[#D4AF37]/25 py-1.5 text-center">
          <p className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">Advertisement</p>
        </div>
        <div className="flex flex-col items-center justify-center text-center px-4 py-8 min-h-[150px]">
          <p className="text-lg text-[#D6D0C5] mb-1">Your ad could be here</p>
          <p className="text-sm text-[#A7B0C0] mb-3">Ads help keep Astra free for everyone.</p>
          {onSubscribe && (
            <button
              data-testid="btn-go-ad-free"
              onClick={() => onSubscribe("an ad-free experience")}
              className="text-sm text-[#04060B] bg-[#D4AF37] hover:bg-[#e6c04e] px-4 py-2 rounded-full transition-colors"
            >
              Go ad-free with Premium
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="ad-banner" className={`relative overflow-hidden rounded-2xl border border-[#D4AF37]/20 bg-[#0B0F19]/50 ${className}`}>
      <div className="bg-[#D4AF37]/10 border-b border-[#D4AF37]/15 py-1.5 text-center">
        <p className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">Advertisement</p>
      </div>
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: "block", minHeight: 150 }}
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={slot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
      {onSubscribe && (
        <button
          onClick={() => onSubscribe("an ad-free experience")}
          className="my-2 block mx-auto text-xs gold-text underline-offset-4 hover:underline"
        >
          Go ad-free with Premium →
        </button>
      )}
    </div>
  );
}
